/**
 * A canvas in a box, sized to it.
 *
 * Every view that draws anything needs the same eight lines: make a renderer,
 * cap the pixel ratio, put the canvas in the mount, size it, keep it sized, and
 * take all of that apart again. Four views had their own copy, byte-identical
 * except for one flag and what each does to its camera on a resize.
 *
 * ── Why this is worth extracting even though it is only plumbing ────────────
 *
 * Not because it is long. Because it is *symmetrical*: four things set up in one
 * place and torn down in another, and a copy that forgets one of the four leaks
 * a WebGL context — which does not fail where it happened, it fails several
 * views later when the browser refuses to make another one. Having the pairing
 * in one place is the whole point.
 */

import * as THREE from 'three';

export interface Viewport {
  renderer: THREE.WebGLRenderer;
  /** Undo everything: the listener, the canvas, the context. */
  dispose: () => void;
}

export interface ViewportOptions {
  /**
   * Depth precision spread logarithmically rather than linearly.
   *
   * Wanted by anything drawing a solar system, where a cockpit rail and a star
   * are in the same frame at a ratio no linear depth buffer survives. The ship
   * editor does not need it — everything it draws is within a few metres.
   */
  logarithmicDepthBuffer?: boolean;
  antialias?: boolean;
  /**
   * Let whatever is behind the canvas show through where nothing was drawn.
   *
   * Wanted by a view that has another layer *underneath* it rather than only on
   * top — flight does, because the crossing gas is its own canvas and the ship
   * has to appear in front of it. Without this the canvas clears to opaque black
   * and the layer below may as well not be there.
   *
   * The clear alpha goes to zero with it. Setting one without the other is a
   * canvas that is transparent in principle and opaque in every frame.
   */
  alpha?: boolean;
}

/**
 * Mount a renderer, and keep it the size of what it is mounted in.
 *
 * `onResize` runs once immediately and then on every window resize, which is
 * what makes it the right place for a camera's aspect ratio: the first call is
 * the initial setup, so the caller does not need a second copy of it. What each
 * view does there differs — most set an aspect, the editor re-frames its subject
 * — and that difference is the reason this is a callback rather than a camera.
 */
export function mountViewport(
  mount: HTMLElement,
  onResize: (width: number, height: number) => void,
  options: ViewportOptions = {},
): Viewport {
  const renderer = new THREE.WebGLRenderer({
    antialias: options.antialias ?? true,
    logarithmicDepthBuffer: options.logarithmicDepthBuffer ?? false,
    alpha: options.alpha ?? false,
  });

  if (options.alpha) renderer.setClearAlpha(0);

  // Capped at two. A phone at three or four renders four times the pixels for a
  // difference nobody can see, and spends the frame budget doing it.
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  mount.appendChild(renderer.domElement);

  const resize = () => {
    const width = mount.clientWidth;
    const height = mount.clientHeight;

    renderer.setSize(width, height);
    onResize(width, height);
  };

  // Once now, so nothing has to duplicate the initial sizing. Without this the
  // renderer keeps its default 300×150 and the whole view draws into a stamp in
  // the corner.
  resize();
  window.addEventListener('resize', resize);

  return {
    renderer,
    dispose: () => {
      window.removeEventListener('resize', resize);
      renderer.dispose();
      // Guarded because a view can be torn down after React has already emptied
      // the mount, and removing a child that is not there throws.
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    },
  };
}
